/**
 * DeleteAccountSection.js — öğrencinin self-servis hesap silme talebini
 * başlattığı bölüm. ProfilePage.js'in "Gizlilik" sekmesinde, Dijital Rozet
 * rıza bölümünün ALTINDA render edilir.
 *
 * Desen (card-clay, AlertDialog, toast) BİREBİR
 * DijitalRozetConsentSection.js'ten alınır — yeni desen icat edilmedi.
 *
 * API sözleşmesi (routes/auth.py::delete_my_account'ta doğrulandı):
 *   DELETE /users/me → {
 *     deleted: true,                  // KORUNDU — başarı sinyali BU alandır
 *     grace_until: null,              // geriye dönük uyumluluk, ARTIK HEP null
 *     account_status: "deactivated",
 *     deactivated_at: <ISO>,
 *     message_tr / message_en
 *   }
 *   403 { error_code: "admin_cannot_self_delete" } — admin/superadmin
 *   kendi hesabını silemez; bu yüzden kart admin/superadmin için baştan
 *   HİÇ render edilmez (garanti hata vermek yerine gizlenir).
 *
 * Bu, MAĞAZA YAYIN BLOKERİ kapsamında geliyor: Apple (2022'den) silme
 * akışının uygulama içinden başlatılabilmesini şart koşuyor, Google Play
 * (2024'ten) Data Safety formunda giriş gerektirmeyen bir web silme URL'i
 * istiyor (bkz. AccountDeletionPage.jsx — public /account-deletion sayfası).
 *
 * İki aşamalı onay (geri dönüşsüz işlem için standart desen):
 *   Dialog A — "emin misin" (nötr Continue, destructive DEĞİL)
 *   Dialog B — son onay + checkbox zorunlu, destructive confirm
 *
 * DAVRANIŞ: hesap ANINDA pasife alınıyor (`account_status="deactivated"`) —
 * öğrenci o andan itibaren giriş yapamıyor (e-posta/şifre, Google, mevcut
 * oturum). Kişisel veriler ise 72 saat içinde GERÇEKTEN siliniyor; süresiz
 * saklama yok. 30 günlük geri alma penceresi KALDIRILDI — `grace_until` alanı
 * yalnızca eski mobil istemciler için duruyor ve her zaman null.
 *
 * İKİ İSTİSNA — metinde açıkça yazılmak ZORUNDA, yazılmazsa eksik ifşa olur:
 *   1. Ödeme / taksit / senet / fatura kayıtları silinmez VE üzerlerindeki
 *      ad, adres, TCKN/VKN maskelenmez (VUK m. 253 ve 256, TTK m. 82).
 *   2. Sertifika kayıtları doğrulanabilirlik için korunur; /verify/{kod}
 *      çalışmaya devam ediyor.
 *
 * ⚠️ KİP KURALI: silme 72 saat GECİKMELİ olduğu için "silindi" gibi geçmiş
 * zaman kipi yanlış vaat olur — "silinir / silinecek ... en geç 3 gün
 * içinde" yazılır.
 *
 * KRİTİK — BAŞARI SİNYALİ: başarı ekranı `r.data.deleted === true` bayrağına
 * bağlıdır, `grace_until`'e DEĞİL. Eskiden `grace_until` doluluğuna bakılıyordu;
 * backend null döndürmeye başlayınca başarı ekranı sessizce hiç render
 * edilmiyordu (kullanıcı "Evet, sil" diyor, hiçbir şey olmuyor, hata da yok).
 * Bu bayrağa geri dönme.
 *
 * Backend deaktivasyonda tüm oturum yollarını kapatır (user_sessions /
 * active_sessions / refresh_tokens / push_tokens — services/account_
 * deactivation.py). Yine de AuthContext.logout() AÇIKÇA çağrılır: yereldeki
 * token/kullanıcı state'ini temizleyen tek yer orası. Otomatik ~2.5sn redirect
 * var ama WCAG 2.2.1 gereği manuel buton ZORUNLU ve tek başına yeterli
 * olmalı — auto-redirect'e güvenilmez.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Trash2,
  LogOut,
  Landmark,
  FileSignature,
  BadgeCheck,
  Building2,
  PhoneCall,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// YEDİ madde; sıra copy spec'e bağlı ve rastgele değil:
//   önce NE OLUYOR      → erişim biter (LogOut), kişisel veriler silinir (Trash2)
//   sonra YASAL İSTİSNA → ödeme/icra (Landmark), sözleşme kopyası (FileSignature)
//   en sonda ÜÇÜNCÜ TARAF / KAMUYA AÇIK GÖRÜNÜRLÜK → sertifikadaki ad
//                          (BadgeCheck), bakanlık kopyası (Building2), lead
//                          kaydı (PhoneCall)
// Silme maddesi İLK İSTİSNADAN ÖNCE gelir: önce ne olduğu, sonra istisnaları.
// Sürpriz değeri en yüksek iki madde — sertifikada adın süresiz herkese açık
// kalması ve bakanlıktaki kopyanın bugün geri çekilmemesi — BİLEREK sonda,
// okuyucunun aklında en taze kalan konumda. Yedi madde birlikte tam ifşayı
// oluşturur; biri çıkarılırsa beyan eksik kalır.
const BULLETS = [
  { icon: LogOut, key: 'profile.deleteAccount.bulletNoLogin' },
  { icon: Trash2, key: 'profile.deleteAccount.bulletDeleted' },
  { icon: Landmark, key: 'profile.deleteAccount.bulletRetained' },
  { icon: FileSignature, key: 'profile.deleteAccount.bulletContract' },
  { icon: BadgeCheck, key: 'profile.deleteAccount.bulletCertificates' },
  { icon: Building2, key: 'profile.deleteAccount.bulletMinistry' },
  { icon: PhoneCall, key: 'profile.deleteAccount.bulletLeads' },
];

const DIALOG_B_DESC_ID = 'delete-account-dialog-b-desc';

export default function DeleteAccountSection() {
  const { user, authHeaders, logout } = useAuth();
  const { lang, t } = useLanguage();
  const toast = useToast();
  const navigate = useNavigate();

  const [dialogAOpen, setDialogAOpen] = useState(false);
  const [dialogBOpen, setDialogBOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deactivated, setDeactivated] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const handleBackHome = useCallback(async () => {
    await logout();
    navigate('/');
  }, [logout, navigate]);

  // Başarılı silme sonrası ~2.5sn otomatik yönlendirme. WCAG 2.2.1 gereği
  // manuel buton ZORUNLU ve tek başına yeterli — bu sadece bir kolaylık.
  useEffect(() => {
    if (!deactivated) return undefined;
    const timer = setTimeout(() => { handleBackHome(); }, 2500);
    return () => clearTimeout(timer);
  }, [deactivated, handleBackHome]);

  const openDialogA = () => setDialogAOpen(true);

  const handleContinueFromA = () => {
    setDialogAOpen(false);
    setDialogBOpen(true);
  };

  const handleCancelB = () => {
    if (deleting) return;
    setDialogBOpen(false);
    setChecked(false);
  };

  const handleConfirmDelete = async () => {
    if (!checked || deleting) return;
    setDeleting(true);
    try {
      const r = await axios.delete(`${API}/users/me`, { headers: authHeaders() });
      setDialogBOpen(false);
      // Başarı sinyali `deleted` bayrağıdır. `grace_until` artık her zaman
      // null döndüğü için ona bakmak başarı ekranını hiç açmazdı.
      setDeactivated(r.data?.deleted === true);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang) || t('profile.deleteAccount.error'));
    } finally {
      setDeleting(false);
    }
  };

  if (isAdmin) return null;

  if (deactivated) {
    return (
      <div className="card-clay p-6 text-center" data-testid="delete-account-success">
        <CheckCircle2 size={36} className="mx-auto text-[var(--brand-success)] mb-3" />
        <h3 className="font-bold text-base text-[var(--text-main)] mb-2">
          {t('profile.deleteAccount.success.title')}
        </h3>
        {/* A11y (WCAG 1.4.3): --text-muted (#718096) beyaz üzerinde 4.02:1 ile
            eşiğin altında kalıyordu. Yıkıcı eylemin hukuki ifşa metni en zor
            okunan metin olamaz → --text-secondary (#4A5568, 7.52:1). */}
        <p className="text-sm text-[var(--text-secondary)] mb-1">
          {t('profile.deleteAccount.success.body')}
        </p>
        <p className="text-xs text-[var(--text-secondary)] mb-4">
          {t('profile.deleteAccount.success.signOutNote')}
        </p>
        <button
          type="button"
          onClick={handleBackHome}
          className="btn-tactile px-5 py-3 min-h-[44px] text-sm bg-white border-[var(--border-light)] inline-flex items-center gap-1.5"
          data-testid="delete-account-back-home"
        >
          {t('profile.deleteAccount.success.backHome')}
        </button>
      </div>
    );
  }

  return (
    <div className="card-clay p-5" data-testid="delete-account-section">
      <div className="flex items-center gap-2 mb-1">
        <Trash2 size={18} className="text-[var(--brand-danger)]" />
        <h3 className="font-bold text-sm">{t('profile.deleteAccount.title')}</h3>
      </div>
      {/* A11y (WCAG 1.4.3): kontrast gerekçesi için başarı ekranındaki nota bak. */}
      <p className="text-xs text-[var(--text-secondary)] mb-4">{t('profile.deleteAccount.subtitle')}</p>

      <ul className="space-y-2.5 mb-5" data-testid="delete-account-bullets">
        {BULLETS.map(({ icon: Icon, key }) => (
          <li key={key} className="flex items-start gap-2 text-xs font-semibold text-[var(--text-main)]">
            <Icon size={14} className="text-[var(--brand-danger)] shrink-0 mt-0.5" />
            <span>{t(key)}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={openDialogA}
        className="btn-tactile px-5 py-3 min-h-[44px] text-sm font-bold text-white bg-[var(--brand-danger-strong)] border-b-4 border-[var(--brand-danger-strong-shadow)] w-full sm:w-auto inline-flex items-center justify-center gap-1.5"
        data-testid="delete-account-cta"
      >
        <Trash2 size={14} aria-hidden="true" />
        {t('profile.deleteAccount.cta')}
      </button>

      {/* Dialog A — ilk (nötr) onay */}
      <AlertDialog open={dialogAOpen} onOpenChange={setDialogAOpen}>
        <AlertDialogContent className="rounded-2xl border-2 border-[var(--border-light)] max-w-md" data-testid="delete-account-dialog-a">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold text-[var(--text-main)]">
              {t('profile.deleteAccount.dialogA.title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[var(--text-secondary)]">
              {t('profile.deleteAccount.dialogA.body')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 mt-2">
            <AlertDialogCancel
              className="rounded-xl border-2 border-[var(--border-light)] font-bold min-h-[44px]"
              data-testid="delete-account-dialog-a-cancel"
            >
              {t('profile.deleteAccount.dialogA.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-xl font-bold min-h-[44px]"
              onClick={(e) => { e.preventDefault(); handleContinueFromA(); }}
              data-testid="delete-account-dialog-a-continue"
            >
              {t('profile.deleteAccount.dialogA.continue')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog B — son, geri dönüşsüz onay + checkbox zorunlu */}
      <AlertDialog open={dialogBOpen} onOpenChange={(open) => { if (!open) handleCancelB(); }}>
        <AlertDialogContent className="rounded-2xl border-2 border-[var(--border-light)] max-w-md" data-testid="delete-account-dialog-b">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold text-[var(--text-main)]">
              {t('profile.deleteAccount.dialogB.title')}
            </AlertDialogTitle>
            <AlertDialogDescription id={DIALOG_B_DESC_ID} className="text-sm text-[var(--text-secondary)]">
              {t('profile.deleteAccount.dialogB.body')}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <label
            htmlFor="delete-account-confirm-checkbox"
            className="flex items-start gap-3 p-3 -mx-3 rounded-lg cursor-pointer hover:bg-black/5"
          >
            <input
              id="delete-account-confirm-checkbox"
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              disabled={deleting}
              aria-describedby={DIALOG_B_DESC_ID}
              className="w-5 h-5 mt-0.5 shrink-0 accent-[var(--brand-danger)]"
              data-testid="delete-account-confirm-checkbox"
            />
            <span className="text-xs font-semibold text-[var(--text-main)]">
              {t('profile.deleteAccount.dialogB.checkboxLabel')}
            </span>
          </label>

          <AlertDialogFooter className="gap-2 mt-2">
            <AlertDialogCancel
              className="rounded-xl border-2 border-[var(--border-light)] font-bold min-h-[44px]"
              disabled={deleting}
              onClick={(e) => { e.preventDefault(); handleCancelB(); }}
              data-testid="delete-account-dialog-b-cancel"
            >
              {t('profile.deleteAccount.dialogB.cancel')}
            </AlertDialogCancel>
            {/* A11y (WCAG 2.1 AA):
                - Dolgu --brand-danger-strong: beyaz metinle 4.98:1 (--brand-danger
                  3.31:1 ile FAIL ediyordu). Token index.css'te.
                - Disabled sinyali YALNIZCA opaklik olamaz (1.4.1 + dusuk kontrast):
                  notr gri dolgu #E5E5E5 + metin #5C5C5C = 5.31:1, ayrica
                  aria-disabled ile programatik olarak da duyurulur.
                - Renk tek sinyal olmasin diye 14px Trash2 ikonu var
                  (metin + ikon + renk). */}
            <AlertDialogAction
              className="rounded-xl font-bold min-h-[44px] bg-[var(--brand-danger-strong)] enabled:hover:bg-[var(--brand-danger-strong)]/90 text-white border-b-4 border-[var(--brand-danger-strong-shadow)] inline-flex items-center justify-center gap-1.5 disabled:bg-[#E5E5E5] disabled:text-[#5C5C5C] disabled:border-[#C4C4C4] disabled:cursor-not-allowed"
              disabled={!checked || deleting}
              aria-disabled={!checked || deleting}
              onClick={(e) => { e.preventDefault(); handleConfirmDelete(); }}
              data-testid="delete-account-dialog-b-confirm"
            >
              {deleting
                ? <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                : <Trash2 size={14} aria-hidden="true" />}
              {deleting ? t('profile.deleteAccount.dialogB.confirming') : t('profile.deleteAccount.dialogB.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
